import { createContext, useContext, type ReactNode } from 'react';
import type { CustomField } from '@kkp-fraud/shared';
import { setActiveCustomFields } from '../fields';

/**
 * Wave 6 — the custom fields of the set whose rules are on screen (`RuleSet.customFields`).
 * `AppLayout` provides the primary set's; the set workspace (`RuleSetRulesRoute`) the set's own.
 * The default is none, so a component mounted on its own (the editor tests, the Blocks harness)
 * sees exactly the standard fields it saw before custom fields existed.
 */
const NONE: readonly CustomField[] = [];
export const CustomFieldsContext = createContext<readonly CustomField[]>(NONE);

/**
 * The custom fields in scope, recorded for the pure field helpers (`kindOf` in `fields.ts`) —
 * every component that draws a field picker calls this first, so the helpers it calls while
 * rendering, and in its handlers, resolve the same set's fields.
 */
export function useCustomFields(): readonly CustomField[] {
  const list = useContext(CustomFieldsContext);
  setActiveCustomFields(list);
  return list;
}

/** Provides `fields` (absent = none) to everything under it. */
export function CustomFieldsScope({ fields, children }: { fields: readonly CustomField[] | undefined; children: ReactNode }) {
  const list = fields ?? NONE;
  setActiveCustomFields(list);
  return <CustomFieldsContext.Provider value={list}>{children}</CustomFieldsContext.Provider>;
}
